import type { Block, DecideReason, FormBlock, FormField, FormLine } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { Caller } from './handles'
import { submitWrite, writeRefusal } from './writes'

/**
 * The three writes on the entitlement matrix, and the one law that decides
 * their shape.
 *
 * # Law 16 is not a note beside these controls, it is which control is dead
 *
 * > *Compliance locks outrank ops settings, visibly, and are disabled for
 * > insufficient roles with the required role named.*
 *
 * Setting an operational rule is `entitlement.write`, which Ops holds. Imposing
 * and lifting a compliance lock are `compliance.lock.write`, which is
 * **Compliance's alone and is not inherited downward** — the role nest runs
 * Support ⊂ Ops ⊂ Compliance in that direction only. So an Ops session gets a
 * live rule control and two dead lock controls, each naming Compliance.
 *
 * # The lift control takes its permission FROM THE ROW
 *
 * `lift_permission` is served on every lock, *"stated as a fact on the row
 * rather than left to the console to derive from the role nest"*. This module
 * therefore never contains a model of who inherits what: it reads the string
 * the backend put on the lock and asks the session whether it holds it. A
 * console carrying its own copy of the nest would be a second implementation of
 * an authorisation rule, on the least reliable surface, kept in step by
 * attention.
 *
 * The fallback when the row does not name one is `compliance.lock.write`, and
 * the control is still drawn dead with that named — not enabled. An unread
 * requirement is not a satisfied one.
 *
 * # There is no control that lifts by setting a flag
 *
 * Imposing and lifting are two endpoints. The impose body has no `lift` field
 * to set false, and the lift body has no `impose` field — so neither control
 * can express the other's act, and there is no single "lock state" control that
 * a future edit could make bidirectional by adding a select. Same shape, and
 * the same reason, as the account-state controls in `accountstate.ts`.
 *
 * # Every act addresses a CELL, never an id
 *
 * Scope, method and direction — because that is the question an operator has
 * ("this method, this direction, this scope, stop refusing") and because there
 * is at most one live lock per cell, guarded twice in migration 099. An id
 * would make the console read the lock before it could lift it.
 *
 * # `enabled` is sent explicitly or not at all
 *
 * On the wire it is a POINTER, and the reason is stated in the backend: with a
 * plain bool an omitted field is `false`, so *"a console that forgot to send it
 * would DISABLE a rail for everyone in the named scope and be told it
 * succeeded"*. The control offers a required select with two values and no
 * default, and the submit sends a real boolean — never an empty string coerced
 * on the far side.
 */

/** `entitlement.write` — setting an operational rule. Ops. */
const RULE_PERM = 'entitlement.write'
const RULE_ROLE = 'Ops'

/**
 * `compliance.lock.write` — imposing and lifting. Compliance's alone.
 *
 * Used as the FALLBACK requirement when a lock row does not name its own, and
 * as the requirement for imposing, which has no row to read it from.
 */
const LOCK_PERM = 'compliance.lock.write'
const LOCK_ROLE = 'Compliance'

/**
 * The scope vocabulary, and the illegal combinations that are refused at the
 * edge rather than resolved.
 *
 * `{"scope": "global", "customer_id": 42}` is the shape the backend refuses by
 * name: built through the constructors it becomes a plain global scope, which
 * on the lift path *"lifts the GLOBAL lock for every customer, returns success,
 * and writes an audit row whose subject is 'global' with no customer id on
 * it"*. The console's own defence is that the three fields are one select and
 * two conditional inputs rather than three free fields — a form that cannot
 * express the contradiction cannot send it.
 */
const SCOPES: readonly (readonly [value: string, label: string])[] = [
  ['global', 'global — everybody'],
  ['segment', 'segment — a named cohort'],
  ['customer', 'customer — one person'],
]

const DIRECTIONS: readonly (readonly [value: string, label: string])[] = [
  ['in', 'inbound — money arriving'],
  ['out', 'outbound — money leaving'],
]

/**
 * Who imposed a lock. Never defaulted, and the reason is the audit.
 *
 * *"This row is what an audit reads to find out on whose authority somebody was
 * restricted, and a defaulted source is this endpoint inventing that answer."*
 * The three values are the backend's closed set.
 */
const LOCK_SOURCES: readonly (readonly [value: string, label: string])[] = [
  ['emi', 'emi — our e-money institution imposed it'],
  ['regulator', 'regulator — a regulator required it'],
  ['internal_compliance', 'internal_compliance — our own compliance decided it'],
]

const RULE_REASONS: readonly DecideReason[] = [
  ['risk_appetite_change', 'The programme’s appetite for this rail changed', ''],
  ['provider_capability_change', 'The rail itself gained or lost the capability', ''],
  ['segment_policy_change', 'The policy for this cohort changed', ''],
  ['customer_request', 'The customer asked for this rail to be opened or closed', ''],
  ['pilot_enrolment', 'Enrolling this scope in a limited rollout', ''],
  ['set_in_error', 'The previous rule should not have been set', ''],
]

const LOCK_REASONS: readonly DecideReason[] = [
  ['sanctions_exposure', 'Sanctions screening requires this rail be stopped', ''],
  ['edd_pending', 'Enhanced due diligence is outstanding', ''],
  ['regulatory_direction', 'A regulator directed it', ''],
  ['institution_direction', 'Our e-money institution directed it', ''],
  ['fraud_investigation', 'A fraud investigation is open on this scope', ''],
]

const LIFT_REASONS: readonly DecideReason[] = [
  ['edd_completed', 'The diligence that prompted it is finished', ''],
  ['sanctions_cleared', 'The screening concern was resolved', ''],
  ['regulatory_direction_withdrawn', 'The direction that required it was withdrawn', ''],
  ['institution_direction_withdrawn', 'Our institution withdrew its direction', ''],
  ['imposed_in_error', 'It should not have been imposed', ''],
]

/**
 * The three fields every act on this screen shares.
 *
 * One function rather than three copies, for the reason `entitlementFilterFrom`
 * is one function on the backend: the two spellings cannot drift, and a diff
 * that changed the scope vocabulary on one control and not the others would
 * look like a tidy-up.
 */
function cellFields(): readonly FormField[] {
  return [
    {
      name: 'scope', label: 'Scope', kind: 'select', required: true, options: SCOPES,
      note: 'Fill in exactly one of the two below to match. A global scope with a customer id is a body the backend refuses by name, because built through its constructors it would silently become a plain global act — and on the lift path that lifts the lock for everybody and reports success.',
    },
    {
      name: 'segment_key', label: 'Segment key', kind: 'text', required: false, mono: true,
      note: 'Only when the scope is segment. Leave empty otherwise.',
    },
    {
      name: 'customer_id', label: 'Customer id', kind: 'text', required: false, mono: true,
      note: 'Only when the scope is customer. Leave empty otherwise.',
    },
    {
      name: 'method_code', label: 'Method', kind: 'text', required: true, mono: true,
      note: 'The payment method code — `sepa`, `sepa_inst`, `swift`. One cell of the matrix is a method and a direction at a scope.',
    },
    {
      name: 'direction', label: 'Direction', kind: 'select', required: true, options: DIRECTIONS,
      note: '`both` is a rail’s capability and never an entitlement: a row meaning both could not be superseded one direction at a time.',
    },
  ]
}

/** Scope, method and direction, as the body wants them. */
function cellBody(v: Readonly<Record<string, string>>): Record<string, unknown> {
  const scope = v['scope'] ?? ''
  const body: Record<string, unknown> = {
    scope,
    method_code: v['method_code'] ?? '',
    direction: v['direction'] ?? '',
  }
  // Sent only where the scope permits it. The backend refuses a body carrying
  // the field the scope does not use, and this is the console half of that:
  // an operator who typed a customer id and then changed the scope to global
  // does not silently send both.
  if (scope === 'segment') body['segment_key'] = v['segment_key'] ?? ''
  if (scope === 'customer') body['customer_id'] = Number(v['customer_id'] ?? '')
  return body
}

/**
 * Setting one cell of the operational layer.
 *
 * This is the control the corpus screen drew as three bulk actions over
 * invented cohorts, one of them announcing a blast radius of "1,318 customers".
 * There is no bulk write on this surface: `POST /entitlements` sets one cell,
 * and a scope IS the bulk — a segment rule governs everybody in that segment
 * without the console enumerating them or claiming a count it did not read.
 */
export function setRuleForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, RULE_PERM, RULE_ROLE, 'Setting an entitlement rule')

  return {
    t: 'form',
    title: 'Set an operational entitlement rule',
    lead: 'One cell of the matrix: a method and a direction at one scope. This is the operational layer — it can permit as well as refuse, and it is outranked by any live compliance lock covering the same cell. Setting a rule does not lift a lock and cannot.',
    fields: [
      ...cellFields(),
      {
        name: 'enabled', label: 'Permit or refuse', kind: 'select', required: true,
        options: [['true', 'allowed — permit this rail'], ['false', 'refused — do not permit it']],
        note: 'No default, deliberately. On the wire this field is a pointer precisely so an omitted value is distinguishable from `false` — a console that left it out would disable a rail for everyone in the scope and be told it worked.',
      },
      {
        name: 'reason_code', label: 'Reason code', kind: 'select', required: true,
        options: RULE_REASONS.map(([c, g]) => [c, `${c} — ${g}`] as const),
        note: 'From the managed registry, and stored on the rule itself — separate from the audit reason below, which is the sentence somebody reads later.',
      },
    ],
    reasons: RULE_REASONS,
    reasonNote: 'Stored on the rule row and in the audit row. A rule closed later keeps this, because the question asked of the table afterwards is what governed and why.',
    auditLine: 'admin.entitlement.set · actor · <scope> · method + direction + reason_code + reason',
    cta: 'Set this cell',
    ctaNote: 'The previous rule for this cell is CLOSED, never deleted — who set it, why, and when it stopped governing all stay readable. Nothing here resolves what any customer may actually do; that happens on the money path.',
    blocked,
    ...(blocked === '' ? { submit: setRuleSubmit(api) } : {}),
  }
}

function setRuleSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>('/entitlements', {
      ...cellBody(v),
      // A real boolean, never the string. The pointer on the wire exists to
      // tell "not sent" from "false", and sending `"false"` would defeat it
      // from the other end.
      enabled: v['enabled'] === 'true',
      reason_code: v['reason_code'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The entitlement rule was set',
    (answer) => panelLines(answer, [
      ['layer', 'operational', 'a live compliance lock on the same cell still refuses'],
    ]),
  )
}

/**
 * Imposing a compliance lock.
 *
 * Compliance's alone. An Ops session sees the whole control with the button
 * dead and `Compliance` named — law 18 — rather than not seeing it, because
 * there IS an escalation path here and somebody can grant it.
 */
export function imposeLockForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, LOCK_PERM, LOCK_ROLE, 'Imposing a compliance lock')

  return {
    t: 'form',
    title: 'Impose a compliance lock',
    lead: 'A lock REFUSES a cell and can do nothing else — there is no value on this form, and no column in the table behind it, that makes a lock grant something. It outranks every operational rule covering the same cell, at every scope.',
    fields: [
      ...cellFields(),
      {
        name: 'source', label: 'On whose authority', kind: 'select', required: true,
        options: LOCK_SOURCES,
        note: 'Never defaulted, because this row is what an institutional audit reads to find out on whose authority somebody was restricted — and it decides the remedy. An EMI lock is lifted by the institution changing its mind, a regulator’s is not lifted by us at all, and an internal one is a decision this programme can revisit.',
      },
      {
        name: 'reason_code', label: 'Reason code', kind: 'select', required: true,
        options: LOCK_REASONS.map(([c, g]) => [c, `${c} — ${g}`] as const),
        note: 'From the managed registry. Stored on the lock and readable by anybody who can read the matrix.',
      },
    ],
    reasons: LOCK_REASONS,
    reasonNote: 'Stored on the lock row. The customer does not see this text; a support agent reading the matrix does.',
    auditLine: 'admin.compliance_lock.impose · actor · <scope> · method + direction + source + reason_code + reason',
    cta: 'Impose the lock',
    ctaNote: 'In force immediately. Lifting is a SEPARATE act on a separate endpoint under the same permission — there is no field on this form that lifts one, and there never will be.',
    blocked,
    ...(blocked === '' ? { submit: lockSubmit(api, 'impose') } : {}),
  }
}

/**
 * Lifting a compliance lock.
 *
 * # The permission comes from the lock, when there is one to read it from
 *
 * `liftPermission` is what the lock row served. Where the screen is showing a
 * specific lock, that string is the requirement; where it is not, the fallback
 * is `compliance.lock.write`. Either way the control is drawn — law 18 — with
 * the role named, because somebody can grant it.
 */
export function liftLockForm(
  api: ApiClient, caller: Caller, liftPermission: string,
): FormBlock {
  // An unread requirement is not a satisfied one: where the row did not name a
  // permission, the fallback is the one the endpoint actually enforces.
  const permission = liftPermission === '' ? LOCK_PERM : liftPermission
  const blocked = writeRefusal(caller, permission, LOCK_ROLE, 'Lifting a compliance lock')

  return {
    t: 'form',
    title: 'Lift a compliance lock',
    lead: 'Names the CELL rather than a lock id: this method, this direction, this scope, stop refusing. There is at most one live lock per cell, so a cell is an unambiguous address — and an id would mean reading the lock before you could lift it.',
    fields: [
      ...cellFields(),
      {
        name: 'reason_code', label: 'Reason code', kind: 'select', required: true,
        options: LIFT_REASONS.map(([c, g]) => [c, `${c} — ${g}`] as const),
        note: 'From the managed registry. Recorded as the CLOSE reason on the lock, beside the reason it was imposed for.',
      },
    ],
    reasons: LIFT_REASONS,
    reasonNote: 'Recorded against the lock’s closure. Who imposed it, who lifted it, and both reasons stay readable afterwards.',
    auditLine: 'admin.compliance_lock.lift · actor · <scope> · method + direction + reason_code + reason',
    cta: 'Lift the lock',
    ctaNote: 'The lock row is CLOSED, never deleted. An audit asks this table whether a restriction was ever imposed, so the evidence that it was has to survive the lift.',
    blocked,
    ...(blocked === '' ? { submit: lockSubmit(api, 'lift') } : {}),
  }
}

function lockSubmit(api: ApiClient, act: 'impose' | 'lift') {
  const path = act === 'impose' ? '/compliance-locks' : '/compliance-locks/lift'
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(path, {
      ...cellBody(v),
      ...(act === 'impose' ? { source: v['source'] ?? '' } : {}),
      reason_code: v['reason_code'] ?? '',
      reason: v['reason'] ?? '',
    }),
    act === 'impose' ? 'The compliance lock is in force' : 'The compliance lock is lifted',
    (answer) => panelLines(answer, []),
  )
}

/**
 * What the backend answered, as lines an operator can quote.
 *
 * The `note` field on both lock responses says something the operator needs and
 * would otherwise have to be told twice — that lifting is a separate call, and
 * that the row survives the lift — so it is surfaced rather than dropped.
 */
function panelLines(answer: unknown, extra: readonly FormLine[]): readonly FormLine[] {
  const lines: FormLine[] = []
  for (const [key, label] of [
    ['scope', 'scope'], ['method_code', 'method'], ['direction', 'direction'],
    ['source', 'on whose authority'], ['state', 'state'],
  ] as const) {
    const v = stringAt(answer, key)
    if (v !== '') lines.push([label, v, ''])
  }
  const note = stringAt(answer, 'note')
  if (note !== '') lines.push(['the backend adds', note, ''])
  return [...lines, ...extra]
}

function stringAt(o: unknown, key: string): string {
  if (typeof o !== 'object' || o === null) return ''
  const v = (o as Record<string, unknown>)[key]
  return typeof v === 'string' ? v : ''
}

/**
 * The three controls, in the order law 16 puts them.
 *
 * The rule control first because it is the one most sessions can use, then the
 * two lock controls — which for an Ops session are two dead controls naming
 * Compliance, and that is the intended rendering rather than a degraded one.
 */
export function entitlementControls(
  api: ApiClient, caller: Caller, liftPermission: string,
): readonly Block[] {
  return [
    setRuleForm(api, caller),
    imposeLockForm(api, caller),
    liftLockForm(api, caller, liftPermission),
  ]
}
